import React, { useState, useEffect } from 'react';
import { Box, Table, Text, Flex, Avatar, Button } from '@radix-ui/themes';
import { useParams, Navigate } from 'react-router-dom';
import api from '../../api/axios.js';
import './Profile.css';

const LearningProgress = () => {
  const [recentViews, setRecentViews] = useState([]);
  const [examScores, setExamScores] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { userId } = useParams(); // Get userId from URL params
  const localUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = localUser.is_admin;
  
  // Security check: Only admins can view other users' progress
  // Regular users can only view their own progress
  const targetUserId = isAdmin ? (userId || localUser.id) : localUser.id;

  useEffect(() => {
    // Redirect if non-admin tries to access another user's progress
    if (!isAdmin && userId && userId !== localUser.id.toString()) {
      setShouldRedirect(true);
      return;
    }
    
    if (targetUserId) {
      fetchUserData();
    } else {
      setError('No user ID available');
      setLoading(false);
    }
  }, [targetUserId, isAdmin, userId, localUser.id]);

  const fetchUserData = async () => {
    try {
      // Fetch user profile with Team and Branch associations
      const userResponse = await api.get(`/users/${targetUserId}`, {
        params: {
          include: ['Team', 'Branch']
        }
      });
      setCurrentUser(userResponse.data);

      // Fetch recent views and exam scores
      const [viewsResponse, scoresResponse] = await Promise.all([
        api.get(`/users/${targetUserId}/lesson-views`),
        api.get(`/users/${targetUserId}/exam-scores`)
      ]);
      
      setRecentViews(viewsResponse.data);
      setExamScores(scoresResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load learning progress');
      setLoading(false);
    }
  };

  const getScoreColor = (score, fullScore) => {
    const percentage = (score / fullScore) * 100;
    if (percentage >= 70) return 'green';
    //if (percentage >= 60) return 'orange';
    return 'red';
  };

  const handleEnableRetest = async (examUserId, lessonId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await api.post(`/exam-users/${examUserId}/enable-retest`, 
        {
          lessonId: lessonId,
          userId: targetUserId
        }
      );
      alert('อนุญาตให้สอบซ่อมเรียบร้อย');
      window.location.reload();
    } catch (error) {
      console.error('Error enabling retest:', error);
      alert('Failed to enable retest');
    }
  };

  if (shouldRedirect) return <Navigate to="/progress" replace />;
  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="profile-container">
      <Box p="4">
        <Flex direction="column" gap="6">
          {currentUser && (
            <Box style={{ padding: '2rem', marginBottom: '1rem', background: 'var(--gray-a2)', borderRadius: '8px' }}>
              <Flex align="center" gap="4">
                <Avatar
                  size="6"
                  src={`https://api.dicebear.com/7.x/initials/svg?seed=${currentUser.firstname?.[0] || ''} ${currentUser.lastname?.[0] || ''}`}
                />
                <Box>
                  <Text size="6" weight="bold">{`${currentUser.firstname || ''} ${currentUser.lastname || ''}`}</Text>
                  <Flex gap="4" mt="2">
                    <Text size="2" color="gray">{currentUser.position || '-'}</Text>
                    <Text size="2" color="gray">•</Text>
                    <Text size="2" color="gray">{currentUser.Branch?.name || '-'}</Text>
                    <Text size="2" color="gray">•</Text>
                    <Text size="2" color="gray">{currentUser.Team?.name || '-'}</Text>
                  </Flex>
                </Box>
              </Flex>
            </Box>
          )}
          <Box style={{ padding: '2rem', marginBottom: '1rem', background: 'var(--green-a3)', borderRadius: '8px' }}>
            <Text size="5" weight="bold" mb="4">เกณฑ์ในการเรียนและการสอบ</Text><br />
            <Text size="2" mb="4">คุณสามารถเลือกเรียนวิชาใดก็ได้ แต่ต้องเรียนและทำข้อสอบให้ผ่านดังนี้ ครึ่งปีจำนวน 10 วิชา ทั้งปีจำนวน 15 วิชา เกณฑ์ในการสอบผ่านคือ 70% ของคะแนนทั้งหมดในแต่ละวิชา</Text>
          </Box>
          <Box>
            <Text size="6" weight="bold" mb="4">บทเรียนที่ดูล่าสุด</Text>
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Lesson Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Last Viewed</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {recentViews.map((view) => (
                  <Table.Row key={view.id}>
                    <Table.Cell>
                      <Text weight="medium">{view.Lesson.name}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(view.last_view_at).toLocaleString()}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
            {recentViews.length === 0 && (
              <Text color="gray">ไม่มีบทเรียนที่ดูล่าสุด</Text>
            )}
          </Box>

          <Box>
            <Text size="6" weight="bold" mb="4">คะแนนสอบ</Text>
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Lesson Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Score</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
                  {isAdmin && <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {examScores.map((exam) => (
                  <Table.Row key={exam.id}>
                    <Table.Cell>
                      <Text weight="medium">{exam.Lesson.name}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text color={getScoreColor(exam.score, exam.full_score)}>
                        {exam.score}/{exam.full_score}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(exam.update_at).toLocaleString()}
                    </Table.Cell>
                    {isAdmin && (
                      <Table.Cell>
                        <Button 
                          size="1"
                          variant="soft"
                          onClick={() => handleEnableRetest(exam.id, exam.Lesson.id)}
                          disabled={exam.is_retest === 1 || getScoreColor(exam.score, exam.full_score) === 'green'}
                        >
                          { getScoreColor(exam.score, exam.full_score) === 'green' 
                            ? 'สอบผ่าน'
                            : exam.is_retest === 1 ? 'สอบซ่อมได้' : 'คลิกเพื่ออนุญาตให้สอบซ่อม'
                          }
                        </Button>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
            {examScores.length === 0 && (
              <Text color="gray">ยังไม่มีการสอบ</Text>
            )}
          </Box>
        </Flex>
      </Box>
    </div>
  );
};

export default LearningProgress;